// Octant zone classification (EVA-29) — Novi Model Engine "Spacing Features".
//
// The enum lives here, beside the colors keyed on it, so the color map can be
// typed on the enum without this module and the classifier importing each
// other. `components/vis/util/octantZone` re-exports it, which is where
// consumers import it from.
export enum OctantZone {
  Lateral = "Lateral",
  Stack = "Stack",
  Stagger = "Stagger"
}

// Band sizes, per unit system. Each system has its OWN canonical numbers —
// the metric pair is NOT a conversion of the imperial pair, it is the round
// metric-native value the EVA-29 acceptance criteria call for ("150 and 400 ft
// for when in Imperial system and 50 and 125 m for when in hybrid").
//
// Consequence, and it is deliberate: classification is NOT invariant across
// unit systems. 50 m is 164 ft, not 150 ft, so a neighbour 160 ft along
// section reads Lateral in Imperial and Stack in Hybrid/SI. Switching the
// user's unit setting can therefore recolor a chip. Do not "fix" this by
// converting one pair into the other — that was the pre-EVA-29 behaviour and
// it contradicts the AC.
export const HOR_MAX_STACK_FT = 150;
export const VERT_MAX_LATERAL_FT = 400;
export const HOR_MAX_STACK_M = 50;
export const VERT_MAX_LATERAL_M = 125;

// Cutoff angles, in degrees. Each bounds ONE zone near the reference well —
// which is why they are separate parameters even though both are 45°:
//   • Hor Cutoff Angle  — how far off horizontal a neighbour may sit and still
//     be a LATERAL. Measured from horizontal.
//   • Vert Cutoff Angle — how far off vertical a neighbour may sit and still be
//     a STACK. Measured from vertical.
// Being angles they are unit-free, so unlike the band sizes above they are
// shared by every unit system. They are carried on OctantThresholds alongside
// the bands because Model Engine exposes all four as one parameter set.
export const HOR_CUTOFF_ANGLE_DEG = 45;
export const VERT_CUTOFF_ANGLE_DEG = 45;

// The two length units the XDA scene is ever expressed in. `BASE_UNITS.Length`
// is "m" and `UNIT_TARGETS.Length` maps Hybrid/SI -> "m", Imperial -> "ft", so
// there is no third case; anything else (including an uninitialised unit
// engine) resolves as metric.
export type OctantLengthUnit = "ft" | "m";

// The band pair for one unit system, as one record — this is the table
// `resolveOctantThresholds` looks up, and the shape a user override replaces
// field-by-field. Each system's numbers stand on their own (see above): this
// is deliberately NOT a base value plus a conversion.
export interface OctantBands {
  readonly hMaxStack: number;
  readonly vMaxLateral: number;
}

export const DEFAULT_OCTANT_BANDS: Record<OctantLengthUnit, OctantBands> = {
  ft: { hMaxStack: HOR_MAX_STACK_FT, vMaxLateral: VERT_MAX_LATERAL_FT },
  m: { hMaxStack: HOR_MAX_STACK_M, vMaxLateral: VERT_MAX_LATERAL_M }
};

// User-edited LENGTH parameters (EVA-163), stored PER UNIT SYSTEM and never
// converted between them. Two reasons this is a map rather than plain numbers:
//
//   1. It is the only shape that keeps the EVA-29 rule above true once the
//      values are editable. A single pair would have to mean something in one
//      unit, and switching the user's unit setting does NOT reset this store —
//      the scene refetches in the new unit while the settings ride along — so
//      "200" typed as feet would silently start reading as 200 metres.
//   2. A dashboard saved by an Imperial user and opened by a metric one shows
//      each of them their own system's numbers, rather than one seeing the
//      other's figures relabelled.
//
// Fields are individually optional and fall back independently, so a user who
// only edits Hor Max of Stack keeps the canonical Vert Max of Lateral.
//
// The four fields are NOT alike in one respect: the two classification bands
// have unit-native defaults in DEFAULT_OCTANT_BANDS, whereas the two
// neighbour-radius caps have NO default at all — absent means "no limit",
// which is the EVA-29 display (every neighbour labelled). Do not invent a
// default for them: seeding one would silently start hiding neighbours on
// dashboards saved before EVA-163.
export interface OctantLengthOverride {
  // Classification bands — see DEFAULT_OCTANT_BANDS.
  hMaxStack?: number;
  vMaxLateral?: number;
  // Neighbour-radius caps (EVA-163 "good to have"): a display filter that
  // drops distant neighbours so their labels stop crowding the widget. They
  // do NOT change what zone a well is, only whether it is drawn.
  //
  // Geometry is taken from the Model Engine zone plots shared on the ticket:
  //   maxNeighborRadius — RADIAL, sqrt(dx^2 + dy^2) <= cap. This is what
  //     makes the plotted region a circle when the two caps are equal, and a
  //     flat-topped barrel when the vertical cap bites: at 1500 radial and
  //     1000 vertical the region is 2 x 1118 wide along its flat top, which is
  //     sqrt(1500^2 - 1000^2) and matches the shared plot. An ellipse would
  //     pinch to a point there and a plain |dx| cap would draw a rectangle;
  //     neither is what the plot shows.
  //   maxVerticalDistance — |dy| <= cap, clipping that circle flat.
  //
  // The pair is named asymmetrically ON PURPOSE (Haydee, EVA-163 comment
  // 163079): the neighbour cap is a RADIUS because it is measured
  // straight-line in any direction, while the vertical one is a plain
  // DISTANCE down a single axis. Calling both "radius" read as though the
  // vertical cap were also diagonal.
  maxNeighborRadius?: number;
  maxVerticalDistance?: number;
}

export type OctantLengthOverrides = {
  [unit in OctantLengthUnit]?: OctantLengthOverride;
};

// Bounds for the editable parameters, enforced at the input AND in the
// resolver — the store is seeded from saved dashboard settings, so a value
// that never passed through the input can still reach the classifier. A band
// of 0 is meaningful (it collapses that band to the axis); a negative one is
// not.
//
// BOTH ends of the angle range are meaningful and reachable, and the Model
// Engine zone plots shared on EVA-163 use both:
//   0 deg  — the cone closes completely, so that class disappears however
//            generous its band is. This is how a user switches a class off.
//   90 deg — the cone stops filtering, so the band alone decides and it keeps
//            its full size.
// They were briefly clamped to 1-89 because the classifier compared tan()
// slopes, where tan(90 deg) is infinite; it compares angles directly now, so
// the endpoints are exact.
export const OCTANT_BAND_MIN = 0;
// A cap of 0 would hide every neighbour, leaving the reference ring and an
// empty plot; the smallest useful cap is therefore above zero. Absent (rather
// than small) is how "no limit" is expressed.
export const SPACING_MAX_RADIUS_MIN = 1;
export const OCTANT_CUTOFF_ANGLE_MIN = 0;
export const OCTANT_CUTOFF_ANGLE_MAX = 90;

// Zone label colors, keyed by OctantZone enum value — typed on the enum so a
// renamed zone is a compile error rather than an undefined chip stroke. Single
// point of change: sampled from the EVA-29 mockups; the stagger amber is
// expected to iterate (EVA-29 comment 162467).
export const OCTANT_ZONE_COLORS: Record<OctantZone, string> = {
  [OctantZone.Lateral]: "#1976D2",
  [OctantZone.Stack]: "#E53935",
  [OctantZone.Stagger]: "#DB9421"
};
